import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Upload, X, FileText, Calendar, Building2 } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useMedicalProviders } from '@/lib/firebase/queries/medicalProviders';
import { toast } from 'sonner';

export interface PendingReport {
  file: File;
  reportDate: string;
  providerId: string;
  providerName: string;
  department: string;
  thumbnailUrl?: string;
}

interface MedicalReportUploaderProps {
  reports: PendingReport[];
  onReportsChange: (reports: PendingReport[]) => void;
  translationPrefix: string;
}

export function MedicalReportUploader({ 
  reports, 
  onReportsChange,
  translationPrefix 
}: MedicalReportUploaderProps) {
  const { t, dir, language } = useLanguage();
  const { providers } = useMedicalProviders();
  const activeProviders = providers.filter(p => p.isActive);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    reportDate: '',
    providerId: '',
    department: '',
  });

  const selectedProvider = activeProviders.find(p => p.id === formData.providerId);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 10 * 1024 * 1024) {
      toast.error(t('common.fileSizeError'));
      return;
    }

    setSelectedFile(file);
    
    // Create preview URL for images/PDFs
    if (file.type.startsWith('image/') || file.type === 'application/pdf') {
      setPreviewUrl(URL.createObjectURL(file));
    } else {
      setPreviewUrl(null);
    }
    
    setFormData({
      reportDate: new Date().toISOString().split('T')[0],
      providerId: '',
      department: '',
    });
    setIsModalOpen(true);
    
    // Reset the input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleAddReport = () => {
    if (!selectedFile || !formData.reportDate || !formData.providerId) {
      return;
    }

    const provider = activeProviders.find(p => p.id === formData.providerId);
    const newReport: PendingReport = {
      file: selectedFile,
      reportDate: formData.reportDate,
      providerId: formData.providerId,
      providerName: provider?.name || '',
      department: formData.department,
      thumbnailUrl: previewUrl || undefined,
    };

    onReportsChange([...reports, newReport]);
    // Don't revoke the preview URL since we're using it as thumbnail
    setIsModalOpen(false);
    setSelectedFile(null);
    setPreviewUrl(null);
    setFormData({
      reportDate: '',
      providerId: '',
      department: '',
    });
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedFile(null);
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
      setPreviewUrl(null);
    }
    setFormData({
      reportDate: '',
      providerId: '',
      department: '',
    });
  };

  const handleRemoveReport = (index: number) => {
    const newReports = reports.filter((_, i) => i !== index);
    onReportsChange(newReports);
  };

  const formatFileSize = (bytes: number) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div>
      {/* Add Report Button */}
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileSelect}
        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
        className="hidden"
      />
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={() => fileInputRef.current?.click()}
        className="gap-2"
      >
        <Upload className="h-4 w-4" />
        {t(`${translationPrefix}.addReport`)}
      </Button>

      {/* Add Report Modal */}
      <Dialog open={isModalOpen} onOpenChange={(open) => { if (!open) handleCloseModal(); }}>
        <DialogContent className="max-w-5xl max-h-[90vh]" dir={dir}>
          <DialogHeader dir={dir}>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              {t(`${translationPrefix}.addReportTitle`)}
            </DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-3 gap-4">
            {/* Preview Section - 2/3 width */}
            <div className="col-span-2 space-y-2">
              <Label>{t(`${translationPrefix}.preview`)}</Label>
              <div className="border rounded-lg h-[500px] flex items-center justify-center bg-muted/30 overflow-hidden">
                {previewUrl ? (
                  selectedFile?.type === 'application/pdf' ? (
                    <iframe src={previewUrl} className="w-full h-full" title="PDF Preview" />
                  ) : (
                    <img src={previewUrl} alt="Preview" className="max-w-full max-h-full object-contain" />
                  )
                ) : (
                  <div className="text-center text-muted-foreground">
                    <FileText className="h-12 w-12 mx-auto mb-2" />
                    <p className="text-sm">{selectedFile?.name}</p>
                    <p className="text-xs">{selectedFile && formatFileSize(selectedFile.size)}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Form Section */}
            <div className="space-y-4">
              {/* File Info */}
              <div className="p-3 bg-muted/50 rounded-lg">
                <div className="font-medium truncate text-sm">{selectedFile?.name}</div>
                <div className="text-xs text-muted-foreground">{selectedFile && formatFileSize(selectedFile.size)}</div>
              </div>

              {/* Report Date */}
              <div className="space-y-2">
                <Label htmlFor="modal-reportDate">{t(`${translationPrefix}.reportDate`)} *</Label>
                <Input
                  id="modal-reportDate"
                  type="date"
                  value={formData.reportDate}
                  onChange={(e) => setFormData(prev => ({ ...prev, reportDate: e.target.value }))}
                  required
                  dir="ltr"
                />
              </div>

              {/* Provider */}
              <div className="space-y-2">
                <Label>{t(`${translationPrefix}.reportProvider`)} *</Label>
                <Select
                  value={formData.providerId}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, providerId: value, department: '' }))}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t(`${translationPrefix}.selectProvider`)} />
                  </SelectTrigger>
                  <SelectContent>
                    {activeProviders.map((provider) => (
                      <SelectItem key={provider.id} value={provider.id}>
                        {language === 'ar' ? (provider.nameAr || provider.name) : provider.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Department */}
              {selectedProvider && selectedProvider.departments.length > 0 && (
                <div className="space-y-2">
                  <Label>{t(`${translationPrefix}.reportDepartment`)}</Label>
                  <Select
                    value={formData.department}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, department: value }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={t(`${translationPrefix}.selectDepartment`)} />
                    </SelectTrigger>
                    <SelectContent>
                      {selectedProvider.departments.map((dept) => (
                        <SelectItem key={dept} value={dept}>
                          {dept}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleCloseModal}>
              {t('common.cancel')}
            </Button>
            <Button 
              type="button" 
              onClick={handleAddReport}
              disabled={!formData.reportDate || !formData.providerId}
            >
              {t(`${translationPrefix}.addReportBtn`)}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
